import { useQuery, useQueryClient, useQueries } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useSnackbar } from "notistack";

import { useLoadingScreen } from '../../Store/LoadingManager';

import QueryBuilderHelper from "../../Utilities/Helpers/QueryBuilderHelper";
import DropdownOptionsHelper from "../../Utilities/Helpers/DropdownOptionsHelper";

import { CachingEnum } from '../../Utilities/Enums/CachingEnum';
import { InstanceCardStatus, InstanceQueryTypeEnum, InstanceStatus, InstanceStatusEnum, InstanceStoredProcedureStatusEnum, InstanceStoredProcedureStatusTextEnum, SpStatus } from "../../Utilities/Enums/StatusEnums";

import { ErrorMessage, SuccessMessage } from "../../Constants/Messages";

import { LegendItemInterface } from "../../Models/Common/List/LegendInterface";

import { DbCloneById, DBCloneCheckById, DeleteDBInstanceById, DownloadInstanceBackupById, EditBackupConfigService, GetBackupConfigById, GetInstanceActivityLogs, GetInstanceAutoBackUps, GetInstanceById, GetInstanceDBInstances, GetInstances, GetInstanceStoredProcedures, GetSummaryInstances, InstanceBackUpNowByIdService, InstanceRefreshAllService, InstanceRefreshByIdService, InstanceSchemaCheckByIdService, RestoreInstanceBackupById } from "../../Api/Service/InstanceManagement/instancemanagementService";
import { InstanceStoredProceduresListField, InstanceSummaryDataField } from "../../Utilities/Enums/ParametersEnum";


export function useInstanceManagementHook(q_queryKey: any, params: any, runInitialFetch: boolean = true, queryType: InstanceQueryTypeEnum = InstanceQueryTypeEnum.INSTANCES, instanceId: string | number = '') {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const DropdownList = [CachingEnum.FREQUENCY_LIST, CachingEnum.RETENTION_DAYS_LIST, CachingEnum.DESTINATION_LIST, CachingEnum.AUTO_DELETE_AFTER_LIST, CachingEnum.DATA_MODE_LIST];
    let { loading, setLoading } = useLoadingScreen();

    // Initial fetch
    const { data, fetchStatus } = useQuery({
        queryKey: q_queryKey,
        queryFn: () => (queryType == InstanceQueryTypeEnum.STORED_PROCEDURES ?
            getInstanceStoreProceduresById(params, instanceId) : (queryType == InstanceQueryTypeEnum.ACTIVITY_LOGS ?
                getInstanceActivityLogsById(params, instanceId) : (queryType == InstanceQueryTypeEnum.AUTO_BACKUP ?
                    getInstanceAutoBackupsById(params, instanceId) : (queryType == InstanceQueryTypeEnum.DATABASE_INSTANCES ?
                        getInstanceDBInstancesById(params, instanceId) : getInstanceList(params)
                    )
                )
            )
        ),
        enabled: runInitialFetch,
    });

    const { data: DropdownData, pending: dropdownFetchStatus } = useQueries({
        queries: DropdownList.map((item) => {
            return {
                queryKey: [item],
                queryFn: () => DropdownOptionsHelper.getLists(item),
            }
        }),
        combine: (results) => {
            const dictionary = Object.assign({}, ...results.map((x, index) => ({ [DropdownList[index]]: x.data })));
            return {
                data: dictionary,
                pending: results.some((result) => result.isPending),
            }
        },
    })

    const instancesInvalidateQueries = async (qkey: any = null) => {
        queryClient.invalidateQueries(qkey != null && qkey != "" ? qkey : q_queryKey);
        return true;
    }
    
    //#region----------------- GET-----------------------
    const getInstanceList = async (params: any) => {
        let _data: any = null;
        let _summaryData: any = null;
        let _legends: any = null;

        const responseData = await GetInstances(QueryBuilderHelper.BuildQueryString(params));
        const responseSummaryData = await GetSummaryInstances(null);

        if (responseData?.isSuccess) {
            _data = responseData?.data
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_FETCHING_DATA), { variant: 'error' })
        }

        if (responseSummaryData?.isSuccess) {
            _summaryData = responseSummaryData?.data?.data ?? null;
        } else {
            enqueueSnackbar(responseSummaryData?.message ?? (responseSummaryData?.data?.message ?? ErrorMessage.PROBLEM_FETCHING_DATA), { variant: 'error' })
        }
        
        _legends = finalizeLegend(_data);
        return { ..._legends, data: _data?.data, cardsLegend: cardsLegend(_summaryData)}
    }

    const finalizeLegend = (_data: any) => {
        const countDict = _data?.data?.metaData?.statusCount
        const totalCount = countDict?.find((x: any | null) => x?.status == '-1')?.count ?? 0
        const legends = ([
            {
                ...InstanceStatus[InstanceStatusEnum.ALL],
                count: totalCount
            },
            {
                ...InstanceStatus[InstanceStatusEnum.ONLINE],
                count: countDict?.find((x: any | null) => x?.status == InstanceStatusEnum.ONLINE)?.count ?? 0
            },
            {
                ...InstanceStatus[InstanceStatusEnum.OFFLINE],
                count: countDict?.find((x: any | null) => x?.status == InstanceStatusEnum.OFFLINE)?.count ?? 0
            },
        ])
        return { legends: legends, totalCount: totalCount}
    }

    const cardsLegend = (_summaryData: any) => {
        const _cardLegends = [
            {
                ...InstanceCardStatus[0],
                count: _summaryData?.[InstanceSummaryDataField.TOTAL_INSTANCES] ?? 0,
                subNotes: `${(_summaryData?.[InstanceSummaryDataField.ON_PREMISE_COUNT] ?? 0)} on-premise . ${(_summaryData?.[InstanceSummaryDataField.CLOUD_COUNT] ?? 0)} cloud`,
            },
            {
                ...InstanceCardStatus[1],
                count: _summaryData?.[InstanceSummaryDataField.ONLINE_COUNT] ?? 0,
                subNotes: `${(_summaryData?.[InstanceSummaryDataField.OFFLINE_COUNT] ?? 0)} offline`,
            },
            {
                ...InstanceCardStatus[2],
                count: _summaryData?.[InstanceSummaryDataField.INTEGRITY_ALERTS] ?? 0,
                subNotes: 'Instances need attention',
            },
            {
                ...InstanceCardStatus[3],
                count: `${(_summaryData?.[InstanceSummaryDataField.BACKUP_ENABLED_COUNT] ?? 0)} / ${(_summaryData?.[InstanceSummaryDataField.BACKUP_COVERAGE_TOTAL] ?? 0)}`,
                subNotes: 'Auto-backup enabled',
            },
        ];

        return _cardLegends;
    }

    const getInstanceById = async (id: string | number) => {
        setLoading(true);

        const responseData = await GetInstanceById(id);

        setLoading(false);
        
        return responseData?.data ?? null;
    }

    const getInstanceStoreProceduresById = async (params: any, id: string | number) => {
        let _data: any = null;
        let _legends: any = null;

        const responseData = await GetInstanceStoredProcedures(QueryBuilderHelper.BuildQueryString(params), id);

        if (responseData?.isSuccess) {
            _data = {
                ...responseData?.data,
                data: {
                    ...responseData?.data?.data,
                    list: [
                        ...responseData?.data?.data?.list?.map((item: any, index: number) => { return { 
                            [InstanceStoredProceduresListField.ROW_ID]: index,
                            ...item,
                        }}) ?? []
                    ],
                }
            }
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_FETCHING_DATA), { variant: 'error' })
        }

        _legends = spLegend(_data);
        return { ..._legends, data: _data?.data}
    }

    const spLegend = (_data: any) => {
        const countDict = _data?.data?.metaData?.statusCount
        const totalCount = countDict?.find((x: any | null) => x?.status == '-1')?.count ?? 0
        
        const legends = ([
            {   
                ...SpStatus?.find(item => item?.id == InstanceStoredProcedureStatusEnum.ALL),
                count: totalCount,
            },
            {   
                ...SpStatus?.find(item => item?.id == InstanceStoredProcedureStatusEnum.VALID),
                count: countDict?.find((x: any | null) => x?.status == InstanceStoredProcedureStatusEnum.VALID)?.count ?? 0,
            },
            {   
                ...SpStatus?.find(item => item?.id == InstanceStoredProcedureStatusEnum.OUTDATED),
                count: countDict?.find((x: any | null) => x?.status == InstanceStoredProcedureStatusEnum.OUTDATED)?.count ?? 0,
            },
            {   
                ...SpStatus?.find(item => item?.id == InstanceStoredProcedureStatusEnum.INVALID),
                count: countDict?.find((x: any | null) => x?.status == InstanceStoredProcedureStatusEnum.INVALID)?.count ?? 0,
            },
            
        ])
        return { legends: legends, totalCount: totalCount}
    }

    const getInstanceAutoBackupsById = async (params: any, id: string | number) => {
        let _data: any = null;
        let _legends: any = null;

        const responseData = await GetInstanceAutoBackUps(QueryBuilderHelper.BuildQueryString(params), id);

        if (responseData?.isSuccess) {
            _data = responseData?.data;
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_FETCHING_DATA), { variant: 'error' })
        }

        // _legends = finalizeLegend(_data);
        return { ..._legends, data: _data?.data}
    }

    const getBackupConfigById = async (id: string | number) => {
        setLoading(true);

        let _data;
        const responseData = await GetBackupConfigById(id);

        setLoading(false);
        
        return responseData?.data ?? null;
    }

    const dbCloneCheckById = async (id: string | number) => {
        setLoading(true);

        let _data;
        const responseData = await DBCloneCheckById(id);

        if(!responseData?.data?.data?.canClone) {
            setLoading(false);
        }
        
        return responseData?.data ?? null;
    }

    const getInstanceDBInstancesById = async (params: any, id: string | number) => {
        let _data: any = null;
        let _legends: any = null;

        const responseData = await GetInstanceDBInstances(QueryBuilderHelper.BuildQueryString(params), id);

        if (responseData?.isSuccess) {
            _data = responseData?.data;
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_FETCHING_DATA), { variant: 'error' })
        }

        // _legends = finalizeLegend(_data);
        return { ..._legends, data: _data?.data}
    }

    const getInstanceActivityLogsById = async (params: any, id: string | number) => {
        let _data: any = null;
        let _legends: any = null;

        const responseData = await GetInstanceActivityLogs(QueryBuilderHelper.BuildQueryString(params), id);

        if (responseData?.isSuccess) {
            _data = responseData?.data;
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_FETCHING_DATA), { variant: 'error' })
        }

        // _legends = finalizeLegend(_data);
        return { ..._legends, data: _data?.data}
    }

    const downloadInstanceBackupById = async (id: string | number, historyId: string | number, isNoLoading: boolean = false, isZipFile: boolean = false) => {
        if(!isNoLoading) {
            setLoading(true);
        }

        const responseData = await DownloadInstanceBackupById(id, historyId, isZipFile);

        if(!isNoLoading) {
            setLoading(false);
        }
        
        return responseData ?? null;
    }
    //#endregion

    //#region----------------- POST-----------------------
    const restoreInstanceBackupById = async (id: string | number, historyId: string | number, isNoLoading: boolean = false) => {
        if(!isNoLoading) {
            setLoading(true);
        }

        const responseData = await RestoreInstanceBackupById(id, historyId);
        
        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            instancesInvalidateQueries([CachingEnum.INSTANCE_ACTIVITY_LOGS_LIST]);
            enqueueSnackbar(responseData?.data?.message ?? SuccessMessage.BACKUP_SUCCESSFULLY_RESTORED, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_BACKUP_RESTORE), { variant: 'error' })
        }

        if(!isNoLoading) {
            setLoading(false);
        }
        
        return responseData ?? null;
    }

    const dbCloneById = async (id: string | number, param: any) => {
        setLoading(true);
        const responseData = await DbCloneById(id, params);

        if (responseData?.isSuccess) {
            enqueueSnackbar(responseData?.data?.message ?? SuccessMessage.DATABASE_INSTANCE_SUCCESSFULLY_CLONED, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_DB_CLONE), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const instanceRefreshAll = async () => {
        setLoading(true);
        const responseData = await InstanceRefreshAllService();
        
        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            enqueueSnackbar(responseData?.data?.message ?? SuccessMessage.INSTANCES_SUCCESSFULLY_REFRESHED, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_REFRESHING_INSTANCES), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const instanceRefreshById = async (id: string | number, preventSetLoading: boolean = false) => {
        setLoading(true);
        const responseData = await InstanceRefreshByIdService(id);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            instancesInvalidateQueries([CachingEnum.INSTANCE_ACTIVITY_LOGS_LIST]);
            enqueueSnackbar(responseData?.data?.message ?? SuccessMessage.INSTANCES_SUCCESSFULLY_REFRESHED, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_REFRESHING_INSTANCES), { variant: 'error' })
            if(preventSetLoading) {
                setLoading(false);
            }
        }

        if(!preventSetLoading) {
            setLoading(false);
        }

        return responseData;
    }

    const instanceBackUpNowById = async (id: string | number, preventSetLoading: boolean = false) => {
        setLoading(true);
        const responseData = await InstanceBackUpNowByIdService(id);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            instancesInvalidateQueries([CachingEnum.INSTANCE_ACTIVITY_LOGS_LIST]);
            enqueueSnackbar(responseData?.data?.message ?? SuccessMessage.INSTANCES_SUCCESSFULLY_BACKEDUP, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_BACKUP_INSTANCES), { variant: 'error' })
            if(preventSetLoading) {
                setLoading(false);
            }
        }

        if(!preventSetLoading) {
            setLoading(false);
        }

        return responseData;
    }

    const instanceSchemaCheckById = async (id: string | number, preventSetLoading: boolean = false) => {
        setLoading(true);
        const responseData = await InstanceSchemaCheckByIdService(id);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            enqueueSnackbar(responseData?.data?.message ?? SuccessMessage.INSTANCES_SUCCESSFULLY_CHECKED_SCHEMA, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_SCHEMA_CHECK_INSTANCES), { variant: 'error' })
            if(preventSetLoading) {
                setLoading(false);
            }
        }

        if(!preventSetLoading) {
            setLoading(false);
        }

        return responseData;
    }
    //#endregion

    //#region----------------- PUT-----------------------
    const editBackupConfigById = async (id: any, params: any) => {
        setLoading(true);

        const responseData = await EditBackupConfigService(id, params);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            enqueueSnackbar(responseData?.data?.message ?? "Auto-backup settings" + SuccessMessage.UPDATED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_UPDATING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }
    //#endregion
    
    //#region----------------- DELETE-----------------------
    const deleteDBInstanceById = async (id: string | number, cloneId: string | number, remarks?: any) => {
        setLoading(true);

        const responseData = await DeleteDBInstanceById(id, cloneId, JSON.stringify({remarks: remarks ?? ""}));

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            instancesInvalidateQueries([CachingEnum.INSTANCE_ACTIVITY_LOGS_LIST]);
            enqueueSnackbar(responseData?.data?.message ?? "Admin User " + SuccessMessage.DELETED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_DELETING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }
    //#endregion

    return {
        data,
        fetchStatus,
        qKey: q_queryKey,
        DropdownData,
        dropdownFetchStatus,
        instancesInvalidateQueries,

        //#region---------- GET----------
        getInstanceById,
        getInstanceStoreProceduresById,
        getBackupConfigById,
        dbCloneCheckById,
        getInstanceActivityLogsById,
        downloadInstanceBackupById,
        //#endregion

        //#region---------- POST----------
        restoreInstanceBackupById,
        dbCloneById,
        instanceRefreshAll,
        instanceRefreshById,
        instanceBackUpNowById,
        instanceSchemaCheckById,
        //#endregion
        
        //#region---------- PUT----------
        editBackupConfigById,
        //#endregion

        //#region---------- DELETE----------
        deleteDBInstanceById,
        //#endregion
    }
}